import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import Cookies from "js-cookie";
import { track } from "../../analytics";
import type {
  AccessRole,
  PaginatedResponse,
  RegistryEpisode,
  RegistrySeries,
} from "../../registry";

function csrfHeaders(): Record<string, string> {
  return { "x-csrftoken": Cookies.get("csrftoken") ?? "" };
}

export async function apiRequest(
  url: string,
  method: string,
  body?: FormData | Record<string, unknown>,
) {
  const isFormData = body instanceof FormData;
  const res = await fetch(url, {
    method,
    headers: {
      ...csrfHeaders(),
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
    },
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.detail || res.statusText);
  }
  if (res.status === 204) return null;
  return res.json();
}

export function useCreateSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) =>
      apiRequest("/api/books/books/", "POST", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      track("series_created", { series_id: data.pk });
    },
  });
}

export function useUpdateSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pk, data }: { pk: number; data: FormData }) =>
      apiRequest(`/api/books/books/${pk}/`, "PATCH", data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
      track("series_updated", { series_id: variables.pk });
    },
  });
}

export function useDeleteSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pk: number) => apiRequest(`/api/books/books/${pk}/`, "DELETE"),
    onSuccess: (_, pk) => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      track("series_deleted", { series_id: pk });
    },
  });
}

export function useCreateEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) =>
      apiRequest("/api/books/chapters/", "POST", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
      track("episode_created", { series_id: data.series });
    },
  });
}

export function useUpdateEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pk, data }: { pk: number; data: FormData }) =>
      apiRequest(`/api/books/chapters/${pk}/`, "PATCH", data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
      track("episode_updated", { episode_id: variables.pk });
    },
  });
}

export function useDeleteEpisode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pk: number) =>
      apiRequest(`/api/books/chapters/${pk}/`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
    },
  });
}

export function useRemoveAssignment(seriesPk: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pk: number) =>
      apiRequest(`/api/books/books/${seriesPk}/assignments/${pk}/`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sharing", seriesPk] });
      track("assignment_removed", { series_id: seriesPk });
    },
  });
}

export function useUpdateAssignmentRole(seriesPk: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pk, role }: { pk: number; role: AccessRole }) =>
      apiRequest(`/api/books/books/${seriesPk}/assignments/${pk}/`, "PATCH", {
        role,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sharing", seriesPk] });
      track("assignment_role_changed", {
        series_id: seriesPk,
        new_role: variables.role,
      });
    },
  });
}

export function useCreateInvitation(seriesPk: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      role,
      expiresIn,
    }: {
      role: AccessRole;
      expiresIn: string;
    }) =>
      apiRequest(`/api/books/books/${seriesPk}/invitations/`, "POST", {
        role,
        expires_in: expiresIn,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sharing", seriesPk] });
      track("invitation_created", {
        series_id: seriesPk,
        role: variables.role,
        expires_in: variables.expiresIn,
      });
    },
  });
}

export function useRevokeInvitation(seriesPk: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pk: number) =>
      apiRequest(`/api/books/books/${seriesPk}/invitations/${pk}/`, "DELETE"),
    onSuccess: (_, pk) => {
      queryClient.invalidateQueries({ queryKey: ["sharing", seriesPk] });
      track("invitation_revoked", { series_id: seriesPk, invitation_id: pk });
    },
  });
}

/** An episode whose number changed as a result of a reorder. */
export interface RenumberedEpisode {
  pk: number;
  label: string;
  oldNumber: number | null;
  newNumber: number;
  /** The episode already has content on disk under `ch{oldNumber}` that must be moved by hand. */
  risky: boolean;
}

interface ReorderResponse {
  ok: boolean;
  renumbered: RenumberedEpisode[];
}

type PreviousRegistryData = [
  QueryKey,
  PaginatedResponse<RegistrySeries> | undefined,
][];

export function useReorderEpisodes(seriesPk: number) {
  const queryClient = useQueryClient();

  return useMutation<
    ReorderResponse,
    Error,
    number[],
    { previousData: PreviousRegistryData }
  >({
    mutationFn: (order: number[]) =>
      apiRequest(`/api/books/books/${seriesPk}/reorder-episodes/`, "POST", {
        order,
      }),

    onMutate: async (order) => {
      await queryClient.cancelQueries({ queryKey: ["registry"] });
      const previousData = queryClient.getQueriesData<
        PaginatedResponse<RegistrySeries>
      >({ queryKey: ["registry"] });

      queryClient.setQueriesData<PaginatedResponse<RegistrySeries>>(
        { queryKey: ["registry"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            results: old.results.map((series) => {
              if (series.pk !== seriesPk) return series;
              const episodeMap = new Map(
                series.episodeSet.map((ep) => [ep.pk, ep]),
              );
              // Provisional only, to avoid a flash of stale numbers mid-drag. The
              // authoritative values come from the refetch in onSettled; the rule
              // itself lives in books/utils/numbering.py.
              let next = 1;
              const reordered = order
                .map((pk) => episodeMap.get(pk))
                .filter((ep): ep is RegistryEpisode => ep !== undefined)
                .sort((a, b) => Number(!!b.isPilot) - Number(!!a.isPilot))
                .map((ep) => ({
                  ...ep,
                  number: ep.isPilot ? 0 : next++,
                }));
              return { ...series, episodeSet: reordered };
            }),
          };
        },
      );

      return { previousData };
    },

    onError: (_err, _order, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["registry"] });
    },
  });
}
